<script>
import CertificateGeneratorIconFeedback from './CertificateGeneratorIconFeedback.vue';
import CertificateGeneratorButton from './CertificateGeneratorButton.vue';

export default {
    name: 'CertificateGeneratorForm',
    components: {
        CertificateGeneratorIconFeedback,
        CertificateGeneratorButton
    },
    props: {
        successfulGenerationMessage: { type: String, required: true },
        waitingGenerationMessage: { type: String, required: true },
        vinRegistrationExistsApi: { type: String, required: true },
        vinNumberText: { type: String, required: true },
        vinNumberHelpText: { type: String, required: true },
        registrationNumberText: { type: String, required: true },
        registrationNumberHelpText: { type: String, required: true },
        generateCertificateButtonText: { type: String, required: true },
        errorGenerationMessage: { type: String, required: true },
        vehicleDataInvalidVinLengthError: { type: String, required: true },
        vehicleDataInvalidVinFormatError: { type: String, required: true },
        vehicleDataInvalidRegistrationLengthError: { type: String, required: true },
        vehicleDataInvalidRegistrationFormatError: { type: String, required: true },
    },
    emits: ['vin-number-validated'],
    data() {
        return {
            vinNumber: '',
            registrationNumber: '',
            loading: false,
            error: false,
            successfullyAdded: false,
            validationError: '',
            vinError: false,
            registrationError: false,
        };
    },

    computed: {
        isResultsShown() {
            return this.loading || this.error || this.successfullyAdded;
        },
    },

    methods: {
        async validateVehicleData() {
            if (!this.isValidVin() || !this.isValidRegistration()) return;
            
            this.resetPreValidatedState();
            await this.validate();
        },

        isValidVin() {
            this.vinError = false;
            
            if (this.vinNumber.length !== 8) {
                this.validationError = this.vehicleDataInvalidVinLengthError;
                this.vinError = true;
                return false;
            }
            
            // Check if VIN contains only alphanumeric characters
            if (!/^[A-Z0-9]+$/i.test(this.vinNumber)) {
                this.validationError = this.vehicleDataInvalidVinFormatError;
                this.vinError = true;
                return false;
            }
            
            return true;
        },

        isValidRegistration() {
            this.registrationError = false;
            
            // Registration is optional, so empty is valid
            if (this.registrationNumber.length === 0) {
                return true;
            }
            
            // Check if registration contains only letters, numbers, and spaces
            if (!/^[A-Z0-9\s]+$/i.test(this.registrationNumber)) {
                this.validationError = this.vehicleDataInvalidRegistrationFormatError;
                this.registrationError = true;
                return false;
            }
            
            // Check length without spaces
            const lengthWithoutSpaces = this.registrationNumber.replace(/\s+/g, '').length;
            
            if (lengthWithoutSpaces < 2 || lengthWithoutSpaces > 8) {
                this.validationError = this.vehicleDataInvalidRegistrationLengthError;
                this.registrationError = true;
                return false;
            }
            
            return true;
        },

        resetStatus() {
            this.error = false;
            this.successfullyAdded = false;
            this.validationError = '';
            this.vinError = false;
            this.registrationError = false;
        },

        resetPreValidatedState() {
            this.loading = true;
            this.resetStatus();
        },

        async validate() {
            try {
                const response = await this.validationRequest();
                if (this.isNotFound(response)) {
                    this.setError();
                    return;
                }
                if (this.isValid(response)) {
                    this.updateVinNumbers(response);
                }
            } catch (error) {
                this.error = true;
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async validationRequest() {
            const response = await fetch(`${this.vinRegistrationExistsApi}?vinRegistration=${this.vinNumber}`);
            if (!response.ok) {
                throw new Error('Something went wrong while validating the chassis number', response.status);
            }
            return await response.json();
        },

        isNotFound(response) {
            return response.result !== 'Found';
        },

        setError() {
            this.loading = false;
            this.error = true;
        },

        isValid(response) {
            return !!response.londonDirectVisionStarRating;
        },

        updateVinNumbers(response) {
            const cleanRegistrationNumber = this.registrationNumber.replace(/\s+/g, '');
            
            const validatedVinData = {
                vinRegistration: this.vinNumber,
                registrationNumber: cleanRegistrationNumber,
                londonDirectVisionStarRating: response.londonDirectVisionStarRating
            };
            this.$emit('vin-number-validated', validatedVinData);
            this.successfullyAdded = true;
        },

        onInputChange() {
            this.resetStatus();
            
            if (this.vinNumber.length === 8) {
                this.isValidVin();
            }
            if (this.registrationNumber.length > 0) {
                this.isValidRegistration();
            }
        }
    },
};
</script>

<template>
    <form class="input-wrapper pt-3" @submit.prevent="validateVehicleData">
        <div class="search-block flex-column">
            <label for="vinNumber">{{ vinNumberText }}</label>
            <div class="search-block__input form-group position-relative">
                <input
                    id="vinNumber"
                    v-model="vinNumber"
                    type="text"
                    :placeholder="vinNumberText"
                    class="form-control"
                    :class="{'is-invalid': error || vinError, 'is-valid': successfullyAdded && !vinError}"
                    @input="onInputChange"
                />
                <CertificateGeneratorIconFeedback v-if="successfullyAdded && !vinError" :is-valid="true" />
                <CertificateGeneratorIconFeedback v-if="error || vinError" :is-valid="false" />
            </div>
            <div class="help-text-block">
                <p>{{ vinNumberHelpText }}</p>
            </div>
            <label for="registrationNumber">{{ registrationNumberText }}</label>
            <div class="search-block__input form-group position-relative">
                <input
                    id="registrationNumber"
                    v-model="registrationNumber"
                    type="text"
                    :placeholder="registrationNumberText"
                    class="form-control"
                    :class="{'is-invalid': registrationError, 'is-valid': successfullyAdded && !registrationError && registrationNumber.length > 0}"
                    @input="onInputChange"
                />
                <CertificateGeneratorIconFeedback v-if="successfullyAdded && !registrationError && registrationNumber.length > 0" :is-valid="true" />
                <CertificateGeneratorIconFeedback v-if="error || registrationError" :is-valid="false" />
            </div>
            <div class="help-text-block">
                <p>{{ registrationNumberHelpText }}</p>
            </div>
            <CertificateGeneratorButton
                :text="generateCertificateButtonText"
                :disabled="vinNumber.length !== 8 || loading || vinError || registrationError"
                :loading="loading"
            />
        </div>
        <div
            v-if="isResultsShown || validationError"
            class="status-field"
            :class="{'is-invalid': error || validationError, 'is-valid': successfullyAdded}"
        >
            <p v-if="loading">{{ waitingGenerationMessage }}</p>
            <div v-else-if="validationError">
                <p>{{ validationError }}</p>
            </div>
            <div v-else-if="error">
                <p>{{ errorGenerationMessage }}</p>
            </div>
            <p v-else-if="successfullyAdded">{{ successfulGenerationMessage }}</p>
        </div>
    </form>
</template>
